import React, { useCallback, useMemo } from "react";
import isHotkey from "is-hotkey";
import { Editable, withReact, Slate } from "slate-react";
import { createEditor } from "slate";
import { withHistory } from "slate-history";
import withHtml from "./withHtml";
import withCorrectVoidBehavior from "./withCorrectVoidBehaviour";
import { toggleMark } from "./buttonActions";
import Element, { getNewParagraph } from "./Element";
import Leaf from "./Leaf";
import EditorToolbar from "./EditorToolbar";
import { Mobile } from "@govlaunch/core";
import EditorSelectionHoverToolbar from "./EditorSelectionHoverToolbar";

const HOTKEYS = {
  "mod+b": "bold",
  "mod+i": "italic",
  "mod+u": "underline",
  "mod+`": "code",
};

function getEditorStyle(readOnly, maxContentHeight) {
  if (readOnly) {
    return {
      height: "100%",
    };
  }

  return {
    maxHeight: maxContentHeight || 600,
    overflowY: "auto",
    height: "100%",
  };
}

export default function SlateEditor({
  value,
  onChange,
  readOnly,
  placeholder,
  minContentHeight,
  maxContentHeight,
  containerStyle,
  extraToolbarIcons,
  onEmailSubscribe,
}) {
  const renderElement = useCallback((props) => <Element onEmailSubscribe={onEmailSubscribe} {...props} />, []);
  const renderLeaf = useCallback((props) => <Leaf {...props} />, []);
  const editor = useMemo(() => withCorrectVoidBehavior(withHtml(withHistory(withReact(createEditor())))), []);
  const slateEditorValue = validateEditorInputValue(value) ? value : [getNewParagraph()];
  // TODO:
  // value prop gets used only once when the editor initializes
  // so if we change it from external source (say we embed a govlaunch story from the admin)
  // nothing is going to happen, so we need to mutate editor.children
  editor.children = slateEditorValue;

  return (
    <div
      css={{
        width: "100%",
        height: "100%",
        ...containerStyle,
      }}
    >
      <Mobile>
        {(isMobile) => (
          <Slate editor={editor} value={slateEditorValue} onChange={(value) => onChange(value)} readOnly={readOnly}>
            {/*
            // 2022-07-15 removing toolbars per request from Keeley
            {!readOnly && !isMobile && <EditorSelectionHoverToolbar />}
            {!readOnly && !isMobile && <EditorHoverToolbar extraToolbarIcons={extraToolbarIcons} />}
            * */}
            {!readOnly && !isMobile && <EditorSelectionHoverToolbar />}
            {!readOnly && <EditorToolbar extraToolbarIcons={extraToolbarIcons} />}

            <div
              css={{
                minHeight: minContentHeight,
              }}
            >
              <Editable
                renderElement={renderElement}
                renderLeaf={renderLeaf}
                placeholder={placeholder || "Type something or insert an element from the toolbar..."}
                readOnly={readOnly}
                spellCheck={false}
                autoFocus={true}
                onKeyDown={(event) => {
                  if (readOnly) {
                    return;
                  }
                  // eslint-disable-next-line no-unused-vars
                  for (const hotkey in HOTKEYS) {
                    if (isHotkey(hotkey, event)) {
                      event.preventDefault();
                      const mark = HOTKEYS[hotkey];
                      toggleMark(editor, mark);
                    }
                  }
                }}
                style={getEditorStyle(readOnly, maxContentHeight)}
              />
            </div>
          </Slate>
        )}
      </Mobile>
    </div>
  );
}

function validateEditorInputValue(value) {
  if (!value || !Array.isArray(value) || value.length === 0) {
    return false;
  }

  for (let i = 0; i < value.length; i++) {
    const node = value[i];

    if (typeof node.type === "undefined" || !node.type) {
      return false;
    }

    if (typeof node.children === "undefined" || !node.children || !Array.isArray(node.children)) {
      return false;
    }

    //TODO: figure out how to recursively validate children object structure
  }

  return true;
}
